class ContactFormBerri extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('#ContactForm');
    if (!this.form) return;

    this.messages = {
      firstNameRequired: this.dataset.firstNameRequired || 'First name is required',
      lastNameRequired: this.dataset.lastNameRequired || 'Last name is required',
      emailRequired: this.dataset.emailRequired || 'Email is required',
      emailInvalid: this.dataset.emailInvalid || 'Email is invalid',
      commentRequired: this.dataset.commentRequired || 'Message is required',
      phoneInvalid: this.dataset.phoneInvalid || 'Enter a valid phone number (10–15 digits)',
    };

    this.errorSummary = this.querySelector('.contact--berri__error-summary');
    this.errorList = this.querySelector('.contact--berri__error-list');
    this.submitButton = this.form.querySelector('#ContactForm-submit');

    this.fields = [
      {
        key: 'firstName',
        input: this.querySelector('#ContactForm-first-name'),
        errorEl: this.querySelector('#ContactForm-first-name-error'),
        label: this.dataset.firstNameLabel || 'First name',
        validate: (value) => (value.trim() ? null : this.messages.firstNameRequired),
      },
      {
        key: 'lastName',
        input: this.querySelector('#ContactForm-last-name'),
        errorEl: this.querySelector('#ContactForm-last-name-error'),
        label: this.dataset.lastNameLabel || 'Last name',
        validate: (value) => (value.trim() ? null : this.messages.lastNameRequired),
      },
      {
        key: 'email',
        input: this.querySelector('#ContactForm-email'),
        errorEl: this.querySelector('#ContactForm-email-error'),
        label: this.dataset.emailLabel || 'Email',
        validate: (value) => {
          const trimmed = value.trim();

          if (!trimmed) return this.messages.emailRequired;
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return this.messages.emailInvalid;

          return null;
        },
      },
      {
        key: 'phone',
        input: this.querySelector('#ContactForm-phone'),
        errorEl: this.querySelector('#ContactForm-phone-error'),
        label: this.dataset.phoneLabel || 'Phone number',
        validate: (value) => this.validatePhone(value),
      },
      {
        key: 'comment',
        input: this.querySelector('#ContactForm-body'),
        errorEl: this.querySelector('#ContactForm-body-error'),
        label: this.dataset.commentLabel || 'Message',
        validate: (value) => (value.trim() ? null : this.messages.commentRequired),
      },
    ].filter((field) => field.input && field.errorEl);

    this.boundHandleSubmit = this.handleSubmit.bind(this);
    this.form.setAttribute('novalidate', 'novalidate');
    this.form.addEventListener('submit', this.boundHandleSubmit, true);

    this.errorList?.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || !this.errorList.contains(link)) return;

      event.preventDefault();
      const targetId = link.getAttribute('href')?.slice(1);
      const target = targetId ? document.getElementById(targetId) : null;
      target?.focus();
    });

    this.fields.forEach((field) => {
      if (field.key === 'phone') {
        field.input.addEventListener('input', () => this.sanitizePhoneInput(field.input));
        field.input.addEventListener('paste', (event) => {
          event.preventDefault();
          const pasted = (event.clipboardData || window.clipboardData).getData('text');
          const digits = pasted.replace(/\D/g, '');
          const input = field.input;
          const start = input.selectionStart ?? input.value.length;
          const end = input.selectionEnd ?? input.value.length;
          const combined = `${input.value.slice(0, start)}${digits}${input.value.slice(end)}`
            .replace(/\D/g, '')
            .slice(0, 15);

          input.value = combined;
          input.setSelectionRange(combined.length, combined.length);
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }

      field.input.addEventListener('input', () => {
        if (!field.validate(field.input.value)) {
          this.clearFieldError(field);
          this.updateErrorSummary();
        }
      });

      field.input.addEventListener('blur', () => {
        const message = field.validate(field.input.value);

        if (message) {
          this.showFieldError(field, message);
          this.updateErrorSummary();
        }
      });
    });
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.boundHandleSubmit, true);
  }

  handleSubmit(event) {
    const errors = this.validateAll();

    if (errors.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showErrors(errors);
      errors[0].input.focus({ preventScroll: true });
      errors[0].input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.hideErrorSummary();

    if (this.submitButton) {
      this.submitButton.disabled = true;
    }
  }

  validatePhone(value) {
    const digits = value.replace(/\D/g, '');

    if (!digits) return null;
    if (digits.length < 10 || digits.length > 15) return this.messages.phoneInvalid;

    return null;
  }

  sanitizePhoneInput(input) {
    const sanitized = input.value.replace(/\D/g, '').slice(0, 15);

    if (input.value !== sanitized) {
      input.value = sanitized;
    }
  }

  validateAll() {
    const errors = [];

    this.fields.forEach((field) => {
      const message = field.validate(field.input.value);

      if (message) {
        errors.push({ ...field, message });
      }
    });

    return errors;
  }

  showErrors(errors) {
    this.fields.forEach((field) => this.clearFieldError(field));

    errors.forEach((error) => {
      this.showFieldError(error, error.message);
    });

    this.updateErrorSummary(errors);
  }

  showFieldError(field, message) {
    field.errorEl.querySelector('.contact__field-error-text').textContent = message;
    field.errorEl.hidden = false;
    field.input.setAttribute('aria-invalid', 'true');
    field.input.setAttribute('aria-describedby', field.errorEl.id);
    field.input.closest('.field')?.classList.add('field--with-error');
    field.input.classList.add('contact--berri__input--error');
  }

  clearFieldError(field) {
    field.errorEl.querySelector('.contact__field-error-text').textContent = '';
    field.errorEl.hidden = true;
    field.input.removeAttribute('aria-invalid');
    field.input.removeAttribute('aria-describedby');
    field.input.closest('.field')?.classList.remove('field--with-error');
    field.input.classList.remove('contact--berri__input--error');
  }

  updateErrorSummary(errors = this.validateAll()) {
    if (!this.errorSummary || !this.errorList) return;

    if (!errors.length) {
      this.hideErrorSummary();
      return;
    }

    this.errorList.innerHTML = errors
      .map(
        (error) =>
          `<li><a href="#${error.input.id}" class="link">${error.label}: ${error.message}</a></li>`
      )
      .join('');

    this.errorSummary.hidden = false;
    this.errorSummary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  hideErrorSummary() {
    if (!this.errorSummary || !this.errorList) return;

    this.errorSummary.hidden = true;
    this.errorList.innerHTML = '';
  }
}

if (!customElements.get('contact-form-berri')) {
  customElements.define('contact-form-berri', ContactFormBerri);
}
